import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { SecurityService } from './security.service';
import { RetailReportsErrorHandler } from './retailReportsErrorHandler';

@Component({
  standalone: false,
  selector: 'app-landing-page',
  template: '<div style="text-align:center;padding:50px;"><h3>Cargando...</h3></div>',
})
export class LandingPageComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly securityService: SecurityService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly errorHandler: RetailReportsErrorHandler
  ) {}

  ngOnInit(): void {
    // Si el usuario no está autenticado, forzar login de Microsoft.
    // Esto cubre el caso de ingreso externo (concesionarios.ford.com → redirect a /)
    // donde el usuario todavía no tiene sesión activa.
    if (!this.authService.isLoggedIn()) {
      this.authService.loginRedirect();
      return;
    }

    // Esperar a que MSAL termine de procesar el token antes de llamar al backend
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.loadClientsAndNavigate();
      });
  }

  private loadClientsAndNavigate(): void {
    // Si viene un temporalClient (ingreso externo desde concesionarios.ford.com)
    // siempre hacer el flujo completo para setear el cliente correcto.
    const hasTemporal = !!this.authService.getTemporalClient();

    // Si navMenuItems ya está populado Y no hay temporalClient, navegar directo
    const firstEnabled = this.authService.navMenuItems.find((e) => e.visible);
    if (firstEnabled && !hasTemporal) {
      this.router.navigate([firstEnabled.routerLink]);
      return;
    }

    // Cargar clientes del backend — MSAL interceptor ya tiene el token listo
    this.securityService.getClients().subscribe({
      next: (clients) => {
        if (!clients || clients.length === 0) {
          this.errorHandler.handleError({ status: 700 });
          return;
        }

        // Mapeo de rutas a roles
        const routeRoleMap: { label: string; routerLink: string; role: string }[] = [
          { label: 'Documentos', routerLink: 'documents-search', role: 'FDD00044' },
          { label: 'Stock',      routerLink: 'dealer-stock-list', role: 'FDP01001' },
          { label: 'Repuestos',  routerLink: 'spare-parts',      role: 'FDI00003' },
        ];

        // Temporal client tiene prioridad, sino buscar el primer cliente
        // que tenga algún rol navegable
        const temporalClient = this.authService.getTemporalClient();
        let selectedClient = temporalClient
          ? clients.find((c) => c.code === +temporalClient.client) || null
          : null;

        if (temporalClient) {
          this.authService.clearTemporalClient();
        }

        // Determinar el primer destino navegable y asegurarse de que
        // el cliente seleccionado tenga ese rol
        let targetRoute: string | null = null;

        for (const entry of routeRoleMap) {
          const clientWithRole = clients.find((c) =>
            c.roles?.some((r) => r.module === entry.role)
          );
          if (clientWithRole) {
            // Si hay temporal client y tiene este rol, usarlo; sino el primero que lo tenga
            if (!selectedClient || !selectedClient.roles?.some((r) => r.module === entry.role)) {
              selectedClient = clientWithRole;
            }
            targetRoute = entry.routerLink;
            break;
          }
        }

        if (!selectedClient) {
          selectedClient = clients[0];
        }

        // Setear el client en sessionStorage ANTES de navegar
        // para que SecurityGuard pueda validar el rol correctamente
        this.authService.setClient(selectedClient);

        this.authService.navMenuItems = routeRoleMap.map((entry) => ({
          label: entry.label,
          routerLink: entry.routerLink,
          visible: selectedClient!.roles?.some((r) => r.module === entry.role) || false,
        }));

        if (targetRoute) {
          this.router.navigate([targetRoute]);
        } else {
          this.errorHandler.handleError({ status: 700 });
        }
      },
      error: () => {
        this.errorHandler.handleError({ status: 700 });
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
